/**
 * Price history line chart (spec sections 17 & 19).
 *
 * Single series, so no legend box — the caption names it. Crosshair + tooltip on
 * hover and on keyboard focus. The caption always states the actual window
 * covered, so the chart never implies more history than was collected.
 */

import { useId, useMemo, useState } from 'react';
import type { PriceHistory } from '@/types/product';
import { formatCurrency, formatDate } from '@/lib/format';

interface Props {
  history: PriceHistory;
  /** Where the series came from, shown in the caption. */
  note: string;
  height?: number;
}

const PAD = { top: 12, right: 12, bottom: 22, left: 46 };

export function PriceHistoryChart({ history, note, height = 180 }: Props): React.JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const titleId = useId();
  const width = 520;

  const geometry = useMemo(() => {
    const points = history.points;
    if (points.length < 2) return null;

    const times = points.map((p) => Date.parse(p.observedAt));
    const amounts = points.map((p) => p.amount);
    const tMin = Math.min(...times);
    const tMax = Math.max(...times);
    const aMin = Math.min(...amounts);
    const aMax = Math.max(...amounts);

    // Pad the value axis so a flat series is not drawn on the frame edge.
    const span = aMax - aMin;
    const yMin = span === 0 ? aMin * 0.95 : aMin - span * 0.15;
    const yMax = span === 0 ? aMax * 1.05 : aMax + span * 0.15;

    const plotW = width - PAD.left - PAD.right;
    const plotH = height - PAD.top - PAD.bottom;

    const x = (t: number): number =>
      tMax === tMin ? PAD.left + plotW / 2 : PAD.left + ((t - tMin) / (tMax - tMin)) * plotW;
    const y = (v: number): number =>
      yMax === yMin ? PAD.top + plotH / 2 : PAD.top + plotH - ((v - yMin) / (yMax - yMin)) * plotH;

    const coords = points.map((p, i) => ({ x: x(times[i]!), y: y(p.amount), point: p }));

    /*
     * Drawn as steps, because that is what a price is.
     *
     * History records the days a price MOVED, not a sample per day — storing
     * an identical figure every morning was 86,000 rows a day describing one
     * unchanged number. Joining those points with straight lines would then
     * draw a slow diagonal drift between two prices that were each flat for
     * weeks: a picture of something that never happened.
     *
     * So each point holds its value until the next one, and the change is the
     * vertical edge. Same data, no invented movement.
     */
    const line = coords
      .map((c, i) =>
        i === 0
          ? `M${c.x.toFixed(1)},${c.y.toFixed(1)}`
          : `H${c.x.toFixed(1)} V${c.y.toFixed(1)}`,
      )
      .join(' ');
    const area = `${line} L${coords[coords.length - 1]!.x.toFixed(1)},${(PAD.top + plotH).toFixed(1)} L${coords[0]!.x.toFixed(1)},${(PAD.top + plotH).toFixed(1)} Z`;

    return {
      coords,
      line,
      area,
      yMin,
      yMax,
      ticks: [yMin, (yMin + yMax) / 2, yMax].map((v) => ({ value: v, y: y(v) })),
      plotH,
    };
  }, [history, height]);

  if (geometry === null) {
    return (
      <figure className="chart-figure">
        <p className="chart-empty">
          {history.points.length === 1
            ? 'Only one price observation so far — a trend needs at least two.'
            : 'No price history has been recorded yet.'}
        </p>
        <figcaption className="chart-caption">{note}</figcaption>
      </figure>
    );
  }

  const active = hover === null ? null : geometry.coords[hover];
  const currency = history.currency;

  return (
    <figure className="chart-figure">
      <div className="chart-wrap">
        <svg
          className="chart-svg"
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-labelledby={titleId}
          onMouseLeave={() => setHover(null)}
        >
          <title id={titleId}>
            Price history from {formatDate(history.windowStart)} to {formatDate(history.windowEnd)}.
            Lowest {formatCurrency(history.min, currency)}, highest{' '}
            {formatCurrency(history.max, currency)}.
          </title>

          {geometry.ticks.map((tick) => (
            <g key={tick.value}>
              <line
                className="chart-grid-line"
                x1={PAD.left}
                x2={width - PAD.right}
                y1={tick.y}
                y2={tick.y}
              />
              <text className="chart-axis-label" x={PAD.left - 6} y={tick.y + 3} textAnchor="end">
                {formatCurrency(tick.value, currency)}
              </text>
            </g>
          ))}

          <path className="chart-area" d={geometry.area} />
          <path className="chart-line" d={geometry.line} />

          {active && (
            <>
              <line
                className="chart-crosshair"
                x1={active.x}
                x2={active.x}
                y1={PAD.top}
                y2={PAD.top + geometry.plotH}
              />
              <circle className="chart-dot" cx={active.x} cy={active.y} r={4.5} />
            </>
          )}

          {/* Hit targets are wider than the marks so hovering is forgiving. */}
          {geometry.coords.map((c, i) => (
            <rect
              key={c.point.observedAt}
              className="chart-hit"
              x={c.x - Math.max(6, (width - PAD.left - PAD.right) / geometry.coords.length / 2)}
              y={PAD.top}
              width={Math.max(12, (width - PAD.left - PAD.right) / geometry.coords.length)}
              height={geometry.plotH}
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
              tabIndex={0}
              role="button"
              aria-label={`${formatDate(c.point.observedAt)}: ${formatCurrency(c.point.amount, currency)}`}
            />
          ))}

          <text className="chart-axis-label" x={PAD.left} y={height - 6}>
            {formatDate(history.windowStart)}
          </text>
          <text className="chart-axis-label" x={width - PAD.right} y={height - 6} textAnchor="end">
            {formatDate(history.windowEnd)}
          </text>
        </svg>

        {active && (
          <div
            className="chart-tooltip"
            style={{
              left: `${(active.x / width) * 100}%`,
              top: 0,
              transform: active.x > width * 0.6 ? 'translateX(-105%)' : 'translateX(5%)',
            }}
          >
            <strong>{formatCurrency(active.point.amount, currency)}</strong>
            <br />
            {formatDate(active.point.observedAt)}
          </div>
        )}
      </div>
      <figcaption className="chart-caption">{note}</figcaption>
    </figure>
  );
}
